import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { apiGet } from "@/lib/api-client";
import { asPercent } from "@/lib/format";
import { pickStableKey } from "@/lib/stable-key";
import { asRecord, asArray } from "@/lib/type-utils";

export function ResolutionCenterPage() {
  const resolutionQuery = useQuery({
    queryKey: ["resolution-center", "resolutions"],
    queryFn: () => apiGet<unknown>("/api/resolution/status"),
    refetchInterval: 30_000,
  });

  const accuracyQuery = useQuery({
    queryKey: ["resolution-center", "accuracy"],
    queryFn: () => apiGet<unknown>("/api/resolution/accuracy"),
    refetchInterval: 60_000,
  });

  const pendingQuery = useQuery({
    queryKey: ["resolution-center", "pending"],
    queryFn: () => apiGet<unknown>("/api/resolution/pending"),
    refetchInterval: 30_000,
  });

  const statusData = asRecord(resolutionQuery.data);
  const accuracyData = asRecord(accuracyQuery.data);
  const pendingData = asRecord(pendingQuery.data);

  const resolutions = asArray(statusData.resolutions);
  const pending = asArray(pendingData.markets);
  // Backend tags fabricated fallback rows with sample:true — they are labeled
  // in the list but never counted as real pending markets.
  const realPending = pending.filter((market) => market.sample !== true);
  const samplePendingCount = pending.length - realPending.length;

  // /status and /accuracy report listener failures in-band as 200 + {error}.
  const statusUnavailable =
    resolutionQuery.isError || typeof statusData.error === "string";
  const accuracyUnavailable =
    accuracyQuery.isError || typeof accuracyData.error === "string";
  const anyQueryError =
    resolutionQuery.isError || accuracyQuery.isError || pendingQuery.isError;
  const inBandError =
    !resolutionQuery.isError && typeof statusData.error === "string"
      ? String(statusData.error)
      : !accuracyQuery.isError && typeof accuracyData.error === "string"
        ? String(accuracyData.error)
        : null;

  const resolvedCount = Number(statusData.resolved_count ?? 0);
  const disputedCount = Number(statusData.disputed_count ?? 0);
  const accuracyRate = Number(accuracyData.accuracy_rate ?? accuracyData.accuracy ?? 0);
  const totalPredictions = Number(accuracyData.total_predictions ?? 0);

  const statusCounts = resolutions.reduce<Record<string, number>>(
    (acc, r) => {
      const status = String(r.status || "unknown").toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    {},
  );

  return (
    <div className="space-y-4 md:space-y-6">
      {anyQueryError ? (
        <QueryErrorState
          what="resolution data"
          onRetry={() => {
            void resolutionQuery.refetch();
            void accuracyQuery.refetch();
            void pendingQuery.refetch();
          }}
        />
      ) : null}

      {inBandError ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">
          Resolution listener reported an error — counts below are unavailable, not zero: {inBandError}
        </div>
      ) : null}

      <Panel
        title="Resolution Center"
        description="Track market resolution status, outcome verification, and prediction accuracy metrics."
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard
            label="Markets Resolved"
            value={statusUnavailable ? "—" : String(resolvedCount)}
            hint={statusUnavailable ? "Status read failed" : "Total resolved markets"}
          />
          <StatCard
            label="Pending Resolution"
            value={pendingQuery.isError ? "—" : String(realPending.length)}
            hint={
              pendingQuery.isError
                ? "Pending read failed"
                : samplePendingCount > 0
                  ? `${samplePendingCount} sample row${samplePendingCount === 1 ? "" : "s"} excluded`
                  : "Awaiting outcome determination"
            }
          />
          <StatCard
            label="Active Disputes"
            value={statusUnavailable ? "—" : String(disputedCount)}
            tone={!statusUnavailable && disputedCount > 0 ? "warning" : "default"}
            hint={statusUnavailable ? "Status read failed" : "Markets under dispute"}
          />
          <StatCard
            label="Resolution Accuracy"
            value={accuracyUnavailable ? "—" : asPercent(accuracyRate)}
            tone={
              accuracyUnavailable
                ? "default"
                : accuracyRate > 0.9
                  ? "success"
                  : accuracyRate > 0.7
                    ? "warning"
                    : "default"
            }
            hint={accuracyUnavailable ? "Accuracy read failed" : "Based on correct predictions"}
          />
          <StatCard
            label="Total Predictions"
            value={accuracyUnavailable ? "—" : String(totalPredictions)}
            hint={accuracyUnavailable ? "Accuracy read failed" : "Predictions tracked for accuracy"}
          />
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="📋 Pending Resolutions" description="Markets awaiting outcome determination">
          <div className="max-h-[400px] space-y-2 overflow-y-auto">
            {pendingQuery.isError ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-amber-300">
                Pending resolutions unavailable — backend unreachable
              </div>
            ) : pending.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No pending resolutions
              </div>
            ) : (
              pending.slice(0, 20).map((market) => {
                const isSample = market.sample === true;
                return (
                  <div
                    key={pickStableKey(market, ["condition_id", "id", "market_id"])}
                    className="rounded-lg bg-gunmetal/50 p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium">{String(market.question || "Unknown Market")}</div>
                        <div className="mono mt-1 text-xs text-slate-400">{String(market.condition_id || market.id || "")}</div>
                      </div>
                      {isSample ? (
                        <span className="rounded-full bg-amber-500/20 px-2 py-1 text-xs text-amber-400">
                          Sample
                        </span>
                      ) : (
                        <span className="rounded-full bg-yellow-500/20 px-2 py-1 text-xs text-yellow-400">
                          Pending
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-slate-400">
                      Resolution Date: {String(market.resolution_date || market.end_date || "TBD")}
                    </div>
                    {isSample ? (
                      <div className="mt-1 text-xs text-amber-400/80">
                        Sample data — not a real market (no live resolution feed)
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </Panel>

        <Panel title="✅ Recently Resolved" description="Latest market outcomes">
          <div className="max-h-[400px] space-y-2 overflow-y-auto">
            {statusUnavailable ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-amber-300">
                Recent resolutions unavailable — resolution status read failed
              </div>
            ) : resolutions.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No recent resolutions
              </div>
            ) : (
              resolutions.slice(0, 20).map((resolution) => {
                const outcome = String(resolution.outcome || "").toLowerCase();
                const isYes = outcome.includes("yes") || outcome === "1";
                const isNo = outcome.includes("no") || outcome === "0";

                return (
                  <div
                    key={pickStableKey(resolution, ["condition_id", "market_id", "id"])}
                    className="rounded-lg bg-gunmetal/50 p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {String(resolution.question || resolution.market || "Unknown Market")}
                        </div>
                        <div className="mono mt-1 text-xs text-slate-400">
                          Resolved: {String(resolution.resolved_at || resolution.date || "Unknown")}
                        </div>
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          isYes
                            ? "bg-green-500/20 text-green-400"
                            : isNo
                              ? "bg-red-500/20 text-red-400"
                              : "bg-slate-500/20 text-slate-400"
                        }`}
                      >
                        {String(resolution.outcome || "Unknown")}
                      </span>
                    </div>
                    {resolution.verification_source != null && (
                      <div className="mt-1 text-xs text-slate-500">
                        Source: {String(resolution.verification_source)}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </Panel>
      </div>

      <Panel title="📊 Resolution Status Breakdown" description="Resolution outcomes by status">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Object.entries(statusCounts).map(([status, count]) => (
            <div key={status} className="rounded-lg bg-gunmetal/50 p-3 text-center">
              <div className="text-2xl font-bold text-gold-400">{count}</div>
              <div className="text-xs capitalize text-slate-400">{status.replace(/_/g, " ")}</div>
            </div>
          ))}
          {Object.keys(statusCounts).length === 0 && (
            <div className="col-span-full rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              {statusUnavailable
                ? "Resolution data unavailable — status read failed"
                : "No resolution data available"}
            </div>
          )}
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Resolution Status API" description="Raw resolution data">
          <JsonView value={resolutionQuery.data} />
        </Panel>
        <Panel title="Accuracy Metrics API" description="Prediction accuracy data">
          <JsonView value={accuracyQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
